import type { DayKey, StreakState } from "./types";

/**
 * Rachas genéricas por día (agnósticas del dominio). Un día "activo" cuenta;
 * un hueco rompe salvo que se gaste una congelación ("freeze"). Puro.
 *
 * Deriva del corazón de habit-streak pero sin acoplarse a su modelo: aquí solo
 * hay un conjunto de días activos + hoy + congelaciones.
 */

const MS_PER_DAY = 86_400_000;

/** `YYYY-MM-DD` (UTC) desde epoch ms. */
export function dayKeyUTC(at: number): DayKey {
  return new Date(at).toISOString().slice(0, 10);
}

export function addDays(day: DayKey, n: number): DayKey {
  const t = Date.parse(`${day}T00:00:00.000Z`) + n * MS_PER_DAY;
  return new Date(t).toISOString().slice(0, 10);
}

/** Días enteros entre dos claves (b - a). */
export function diffDays(a: DayKey, b: DayKey): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / MS_PER_DAY);
}

export interface StreakOpts {
  /** Congelaciones disponibles para tapar huecos de 1 día. */
  freezes?: number;
}

/**
 * Calcula la racha (actual + récord) a partir del set de días activos y `today`.
 * `today` sin actividad NO rompe (queda pendiente). Cada hueco de días consume
 * congelaciones (1 por día ausente) mientras queden; si se agotan, rompe.
 */
export function computeStreak(
  activeDays: Iterable<DayKey>,
  today: DayKey,
  opts: StreakOpts = {},
): StreakState {
  const days = [...new Set(activeDays)].filter((d) => d <= today).toSorted();
  if (days.length === 0) {
    return { current: 0, best: 0 };
  }

  // Récord: recorre en orden contando runs consecutivos.
  let best = 1;
  let run = 1;
  for (let i = 1; i < days.length; i += 1) {
    const gap = diffDays(days[i - 1]!, days[i]!);
    run = gap === 1 ? run + 1 : 1;
    if (run > best) {
      best = run;
    }
  }

  // Racha actual: cuenta hacia atrás desde el último día activo, tolerando
  // huecos con congelaciones. Se ancla en hoy o ayer (si hoy aún sin marcar).
  const set = new Set(days);
  const last = days[days.length - 1]!;
  const sinceLast = diffDays(last, today);
  let freezes = opts.freezes ?? 0;
  // Si el último día activo está a más de (1 + freezes) de hoy, la racha ya murió.
  if (sinceLast > 1 + freezes) {
    return { current: 0, best };
  }
  freezes -= Math.max(0, sinceLast - 1); // el hueco hoy↔último consume freezes

  let current = 0;
  let cursor: DayKey = last;
  while (true) {
    if (set.has(cursor)) {
      current += 1;
      cursor = addDays(cursor, -1);
      continue;
    }
    // Hueco: intenta taparlo con una congelación.
    if (freezes > 0) {
      freezes -= 1;
      cursor = addDays(cursor, -1);
      continue;
    }
    break;
  }
  return { current, best: Math.max(best, current) };
}
